// // src/hooks/useInfiniteList.ts
// 'use client';

// import { useCallback, useEffect, useRef, useState } from 'react';
// import { useInView } from 'react-intersection-observer';

// export type ResetKind = 'keyword' | 'tab' | 'order' | 'group' | 'other';

// export type FetchParams = {
//   page: number;
//   pageSize?: number;
//   signal?: AbortSignal;
// };

// export type FetchResult<T> = {
//   items: T[];
//   /** 若後端有回 total 可以給，沒有就不用 */
//   total?: number;
//   /** 若後端直接告訴你還有沒有下一頁，可以給 hasMore */
//   hasMore?: boolean;
// };

// export type UseInfiniteListOptions<T> = {
//   /** 依 page 取得資料 */
//   fetchData: (params: FetchParams) => Promise<FetchResult<T>>;

//   /** 預設每頁幾筆（第一次載也是這個數） */
//   pageSize?: number;

//   /** 條件改變時會 reset；通常可用 keyword / tab 等 */
//   resetKey?: unknown;

//   /** 本次 reset 原因（用在 skeleton 策略） */
//   resetKind?: ResetKind; // 先保留 type，之後要用再從參數解構出來

//   /** IntersectionObserver 用的 rootMargin（預設 '0px'） */
//   rootMargin?: string;
// };

// export type UseInfiniteListReturn<T> = {
//   items: T[];
//   loading: boolean;
//   error: boolean;
//   booting: boolean;
//   hasMore: boolean;
//   /** 目前 page（下一次要載的頁碼） */
//   page: number;
//   /** call 一次會載入下一頁 */
//   loadMore: () => Promise<void>;
//   /** IntersectionObserver 用的 ref，丟到畫面底部哨兵即可 */
//   sentinelRef: (node?: Element | null) => void;
//   /** 上一輪 items 筆數，用來算 skeleton */
//   prevCount: number;
// };

// /**
//  * 可跨頁共用的 Infinite List hook
//  * - 第一次會自動載一頁
//  * - inView 變 true 時會載下一頁
//  * - resetKey 變化時會重新載入
//  */
// export function useInfiniteList<T>({
//   fetchData,
//   pageSize = 10,
//   resetKey,
//   // resetKind = 'other',  // ❌ 用不到先不要解構，避免 no-unused-vars
//   rootMargin = '0px',
// }: UseInfiniteListOptions<T>): UseInfiniteListReturn<T> {
//   const [items, setItems] = useState<T[]>([]);
//   const [page, setPage] = useState(1);
//   const [hasMore, setHasMore] = useState(true);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(false);
//   const [booting, setBooting] = useState(true);

//   const { ref, inView } = useInView({ rootMargin });
//   const prevCountRef = useRef(0);
//   const inFlightRef = useRef(false);
//   const abortRef = useRef<AbortController | null>(null);

//   // resetKey 變動時重置
//   useEffect(() => {
//     prevCountRef.current = items.length;
//     setItems([]);
//     setPage(1);
//     setHasMore(true);
//     setError(false);
//     setBooting(true);
//     inFlightRef.current = false;
//     abortRef.current?.abort();
//     abortRef.current = null;
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [resetKey]);

//   const loadMore = useCallback(async () => {
//     if (inFlightRef.current || loading || !hasMore) return;

//     inFlightRef.current = true;
//     setLoading(true);
//     setError(false);

//     abortRef.current?.abort();
//     const controller = new AbortController();
//     abortRef.current = controller;

//     try {
//       const result = await fetchData({
//         page,
//         pageSize,
//         signal: controller.signal,
//       });

//       const newItems = result.items ?? [];

//       // 優先使用後端回傳的 hasMore / total
//       let nextHasMore = true;
//       if (typeof result.hasMore === 'boolean') {
//         nextHasMore = result.hasMore;
//       } else if (typeof result.total === 'number') {
//         nextHasMore = page * pageSize < result.total;
//       } else {
//         // 沒有 total / hasMore，就只要這一頁有東西就繼續
//         nextHasMore = newItems.length > 0;
//       }

//       setItems((prev) => [...prev, ...newItems]);
//       setHasMore(nextHasMore);
//       setPage((prev) => prev + 1);
//     } catch {
//       // err 不需要用到，就不要宣告參數，避免 no-unused-vars
//       if (!controller.signal.aborted) {
//         setError(true);
//       }
//     } finally {
//       setLoading(false);
//       setBooting(false);
//       inFlightRef.current = false;
//     }
//   }, [fetchData, hasMore, loading, page, pageSize]);

//   // 初次 / reset 後載入第一頁
//   useEffect(() => {
//     if (page === 1 && items.length === 0 && !loading) {
//       loadMore();
//     }
//   }, [page, items.length, loading, loadMore]);

//   // 觀察 inView → 自動載下一頁
//   useEffect(() => {
//     if (inView) {
//       loadMore();
//     }
//   }, [inView, loadMore]);

//   return {
//     items,
//     loading,
//     error,
//     booting,
//     hasMore,
//     page,
//     loadMore,
//     sentinelRef: ref,
//     prevCount: prevCountRef.current,
//   };
// }

// src/hooks/useInfiniteList.ts
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

export type ResetKind = 'keyword' | 'tab' | 'order' | 'group' | 'other';

export type FetchParams = {
  page: number;
  pageSize?: number;
  signal?: AbortSignal;
};

export type FetchResult<T> = {
  items: T[];
  total?: number;
  hasMore?: boolean;
};

export type UseInfiniteListOptions<T> = {
  fetchData: (params: FetchParams) => Promise<FetchResult<T>>;
  pageSize?: number;
  resetKey?: unknown;
  resetKind?: ResetKind;
  rootMargin?: string;

  /**
   * ✅ 用來做全域去重（強烈建議傳）
   * - InfiniteListWithFetch 會把它傳進來
   * - 這樣 useInfiniteList 層就能避免 items state 自己變成重複堆疊
   */
  getKey?: (item: T, index: number) => string | number;
};

export type UseInfiniteListReturn<T> = {
  items: T[];
  loading: boolean;
  error: boolean;
  booting: boolean;
  hasMore: boolean;
  page: number;
  loadMore: () => Promise<void>;
  sentinelRef: (node?: Element | null) => void;
  prevCount: number;
};

function normKey(v: string | number): string {
  return String(v).trim();
}

export function useInfiniteList<T>({
  fetchData,
  pageSize = 10,
  resetKey,
  rootMargin = '0px',
  getKey,
}: UseInfiniteListOptions<T>): UseInfiniteListReturn<T> {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [booting, setBooting] = useState(true);

  const { ref, inView } = useInView({ rootMargin });

  const prevCountRef = useRef(0);
  const inFlightRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  /** ✅ 防止同一頁被重複 append（即使你不小心重抓同一頁） */
  const loadedPagesRef = useRef<Set<number>>(new Set());

  /** ✅ 全域去重 key（跨頁） */
  const seenKeysRef = useRef<Set<string>>(new Set());

  // resetKey 變動時重置
  useEffect(() => {
    prevCountRef.current = items.length;

    setItems([]);
    setPage(1);
    setHasMore(true);
    setError(false);
    setBooting(true);

    inFlightRef.current = false;
    loadedPagesRef.current.clear();
    seenKeysRef.current.clear();

    abortRef.current?.abort();
    abortRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const loadMore = useCallback(async () => {
    if (inFlightRef.current || loading || !hasMore) return;

    // ✅ 若同頁已成功載過，就不要再載（可擋掉一堆奇怪重入）
    if (loadedPagesRef.current.has(page)) return;

    inFlightRef.current = true;
    setLoading(true);
    setError(false);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await fetchData({
        page,
        pageSize,
        signal: controller.signal,
      });

      const fetched = result.items ?? [];

      // ✅ 先算 hasMore（用「原始 fetched 長度」判斷，不要用去重後長度）
      let nextHasMore = true;
      if (typeof result.hasMore === 'boolean') {
        nextHasMore = result.hasMore;
      } else if (typeof result.total === 'number') {
        nextHasMore = page * pageSize < result.total;
      } else {
        nextHasMore = fetched.length > 0;
      }

      // ✅ 全域去重（跨頁 / 跨重抓 / 後端重複）
      let appendList = fetched;
      if (getKey) {
        const out: T[] = [];
        for (let i = 0; i < fetched.length; i++) {
          const k0 = getKey(fetched[i], i);
          const k = normKey(k0);
          if (!k) {
            out.push(fetched[i]);
            continue;
          }
          if (seenKeysRef.current.has(k)) continue;
          seenKeysRef.current.add(k);
          out.push(fetched[i]);
        }
        appendList = out;
      }

      // ✅ 標記此 page 已完成（成功才算）
      loadedPagesRef.current.add(page);

      setItems((prev) => [...prev, ...appendList]);
      setHasMore(nextHasMore);
      setPage((prev) => prev + 1);
    } catch {
      if (!controller.signal.aborted) {
        setError(true);
      }
    } finally {
      setLoading(false);
      setBooting(false);
      inFlightRef.current = false;
    }
  }, [fetchData, getKey, hasMore, loading, page, pageSize]);

  // 初次 / reset 後載入第一頁
  useEffect(() => {
    if (page === 1 && items.length === 0 && !loading) {
      loadMore();
    }
  }, [page, items.length, loading, loadMore]);

  // 觀察 inView → 自動載下一頁
  useEffect(() => {
    if (inView) {
      loadMore();
    }
  }, [inView, loadMore]);

  return {
    items,
    loading,
    error,
    booting,
    hasMore,
    page,
    loadMore,
    sentinelRef: ref,
    prevCount: prevCountRef.current,
  };
}
